import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useProjectDashboard, useIssueDetail, useIssueComments, useAddIssueComment, useSendTeamMemberDM } from '@/hooks/useProjectDashboard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { AssignPicker, useCanAssign } from '@/components/common/AssignPicker';
import {
  AlertTriangle,
  GitBranch,
  GitCommit,
  GitMerge,
  GitPullRequest,
  ExternalLink,
  MessageSquare,
  Send,
  Clock,
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
  X,
  Star,
  Users,
  FolderGit2,
  Plus,
} from 'lucide-react';
import { formatRelativeTime } from '@/utils/helpers';
import { newIssueUrl } from '@/utils/issue-template';
import {
  describeOutcome,
  IssueSummary,
  MergeRequestSummary,
  StaleIssue,
  IssueByAssignee,
} from '@/api/project-dashboard.api';

/**
 * Status badge for issues/MRs
 */
const STATUS_STYLE: Record<string, { title: string; className: string }> = {
  todo: { title: 'To do', className: 'bg-gray-100 text-gray-700' },
  'in-progress': { title: 'In progress', className: 'bg-blue-100 text-blue-800' },
  'in-review': { title: 'In review', className: 'bg-violet-100 text-violet-800' },
  test: { title: 'Test', className: 'bg-amber-100 text-amber-800' },
  blocked: { title: 'Blocked', className: 'bg-red-100 text-red-700' },
  'needs-info': { title: 'Needs info', className: 'bg-red-100 text-red-700' },
  'needs-evidence': { title: 'Needs evidence', className: 'bg-red-100 text-red-700' },
};

/** Where an open issue is in the workflow (its status label), the same as the board columns */
function StatusBadge({ status }: { status?: string }) {
  const style = status ? STATUS_STYLE[status] : undefined;
  if (!style) return null;
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${style.className}`}>{style.title}</span>;
}

function StateBadge({ state }: { state: string }) {
  const config: Record<string, { icon: typeof CheckCircle; className: string }> = {
    opened: { icon: AlertCircle, className: 'bg-green-100 text-green-800' },
    closed: { icon: XCircle, className: 'bg-gray-100 text-gray-800' },
    merged: { icon: GitMerge, className: 'bg-purple-100 text-purple-800' },
  };

  const { icon: Icon, className } = config[state] || config.opened;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      <Icon className="h-3 w-3" />
      {state}
    </span>
  );
}

/**
 * Stale warning badge
 */
function StaleBadge({ days }: { days: number }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
      <AlertTriangle className="h-3 w-3" />
      {days}d stale
    </span>
  );
}

/**
 * Summary stat card
 */
function StatCard({ label, value, icon: Icon, variant = 'default' }: {
  label: string;
  value: number;
  icon: typeof CheckCircle;
  variant?: 'default' | 'warning' | 'success' | 'danger';
}) {
  const variantClasses = {
    default: 'text-primary',
    warning: 'text-orange-600',
    success: 'text-green-600',
    danger: 'text-red-600',
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
      <Icon className={`h-5 w-5 ${variantClasses[variant]}`} />
      <div>
        <div className="text-2xl font-bold">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

/**
 * Issue row component
 */
function IssueRow({ issue, onClick }: { issue: IssueSummary; onClick: () => void }) {
  return (
    <div
      className="flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg cursor-pointer border-b last:border-0"
      onClick={onClick}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <StateBadge state={issue.state} />
          {issue.state === 'opened' && <StatusBadge status={issue.status} />}
          {issue.labels.includes('qa-approved') && (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">QA approved</span>
          )}
          {issue.isStale && <StaleBadge days={issue.daysSinceUpdated} />}
          <span className="text-xs text-muted-foreground">{issue.repoName}</span>
        </div>
        <h4 className="font-medium truncate">#{issue.iid} {issue.title}</h4>
        <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
          {issue.assignees.length > 0 ? (
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {issue.assignees.map(a => a.username).join(', ')}
            </span>
          ) : (
            <span className="text-orange-600">Unassigned</span>
          )}
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatRelativeTime(issue.updatedAt)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <a
          href={issue.webUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="p-1 hover:bg-muted rounded"
        >
          <ExternalLink className="h-4 w-4 text-muted-foreground" />
        </a>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </div>
    </div>
  );
}

/**
 * MR row component
 */
function MRRow({ mr }: { mr: MergeRequestSummary }) {
  return (
    <div className="flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg border-b last:border-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <StateBadge state={mr.state} />
          {mr.isDraft && (
            <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">Draft</span>
          )}
          {mr.hasConflicts && (
            <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-600">Conflicts</span>
          )}
          {mr.approvedBy && mr.approvedBy.length > 0 && (
            <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800" title="Approved in GitLab">
              ✓ approved by {mr.approvedBy.map((u) => `@${u}`).join(', ')}
            </span>
          )}
          <span className="text-xs text-muted-foreground">{mr.repoName}</span>
        </div>
        <h4 className="font-medium truncate">!{mr.iid} {mr.title}</h4>
        <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
          <span className="flex items-center gap-1">
            <GitBranch className="h-3 w-3" />
            {mr.sourceBranch} → {mr.targetBranch}
          </span>
          <span className="flex items-center gap-1">
            <User className="h-3 w-3" />
            {mr.author.username}
          </span>
          {mr.notesCount > 0 && (
            <span className="flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              {mr.notesCount}
            </span>
          )}
        </div>
      </div>
      <a
        href={mr.webUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="p-1 hover:bg-muted rounded"
      >
        <ExternalLink className="h-4 w-4 text-muted-foreground" />
      </a>
    </div>
  );
}

/**
 * Stale issues alert
 */
function StaleIssuesAlert({ issues, onSelect }: { issues: StaleIssue[]; onSelect: (issue: StaleIssue) => void }) {
  if (issues.length === 0) return null;

  return (
    <Card className="border-orange-200 bg-orange-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2 text-orange-800">
          <AlertTriangle className="h-5 w-5" />
          Stale Issues ({issues.length})
        </CardTitle>
        <CardDescription className="text-orange-700">
          These issues haven't been updated recently and may need attention
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {issues.slice(0, 5).map(issue => (
            <div
              key={`${issue.repoName}-${issue.iid}`}
              className="flex items-center justify-between p-2 bg-white rounded cursor-pointer hover:bg-gray-50"
              onClick={() => onSelect(issue)}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">#{issue.iid} {issue.title}</div>
                <div className="text-xs text-muted-foreground">
                  {issue.repoName} • {issue.daysSinceUpdated}d since update
                  {issue.assignees.length > 0 && ` • ${issue.assignees.map(a => a.username).join(', ')}`}
                </div>
              </div>
              <a
                href={issue.webUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-1 hover:bg-muted rounded"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Issues by assignee section
 */
function IssuesByAssignee({ data }: { data: IssueByAssignee[] }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="h-5 w-5" />
          Issues by Assignee
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {data.map(item => (
            <div key={item.user.username} className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{item.user.name || item.user.username}</span>
                  <span className="text-sm text-muted-foreground">{item.count} issues</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 space-y-1">
                  {item.issues.map(issue => (
                    <div key={issue.iid} className="flex items-center gap-1">
                      {issue.isStale && <AlertTriangle className="h-3 w-3 text-orange-500" />}
                      <span className="truncate">#{issue.iid} {issue.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Issue detail panel
 */
function IssueDetailPanel({
  projectId,
  repoId,
  issueIid,
  onClose,
}: {
  projectId: string;
  repoId: string;
  issueIid: number;
  onClose: () => void;
}) {
  const [comment, setComment] = useState('');
  const [dmContent, setDmContent] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState<string | null>(null);

  const { data, isLoading } = useIssueDetail(projectId, repoId, issueIid);
  const canAssign = useCanAssign(data?.issue.state === 'opened');
  const { data: comments, isLoading: commentsLoading } = useIssueComments(projectId, repoId, issueIid, 20);
  const addCommentMutation = useAddIssueComment();
  const sendDMMutation = useSendTeamMemberDM();

  const [notice, setNotice] = useState<string | null>(null);

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    const outcome = await addCommentMutation.mutateAsync({ projectId, repoId, issueIid, body: comment });
    setNotice(describeOutcome(outcome));
    setComment('');
  };

  const handleSendDM = async () => {
    if (!dmContent.trim() || !selectedAssignee) return;
    const outcome = await sendDMMutation.mutateAsync({
      projectId,
      userId: selectedAssignee,
      content: dmContent,
      context: { issueTitle: data?.issue.title },
    });
    setNotice(describeOutcome(outcome));
    setDmContent('');
    setSelectedAssignee(null);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end">
      <div className="w-full max-w-2xl bg-background h-full flex flex-col shadow-xl overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-muted/50">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate">
              {data?.issue ? `#${data.issue.iid} ${data.issue.title}` : 'Loading...'}
            </h3>
            {data?.repo && (
              <p className="text-sm text-muted-foreground">{data.repo.name}</p>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : data ? (
          <div className="flex-1 overflow-y-auto">
            {/* Issue info */}
            <div className="p-4 border-b space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <StateBadge state={data.issue.state} />
                {data.issue.isStale && <StaleBadge days={data.issue.daysSinceUpdated} />}
                {data.issue.labels.map(label => (
                  <span key={label} className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                    {label}
                  </span>
                ))}
              </div>

              {/* Description */}
              {data.issue.description && (
                <div className="bg-muted/50 rounded-lg p-3">
                  <h4 className="text-sm font-medium mb-2">Description</h4>
                  <div className="text-sm text-muted-foreground whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {data.issue.description}
                  </div>
                </div>
              )}

              <div className="text-sm text-muted-foreground space-y-1">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Created {formatRelativeTime(data.issue.createdAt)} • Updated {formatRelativeTime(data.issue.updatedAt)}
                </div>
                {data.issue.dueDate && (
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Due: {new Date(data.issue.dueDate).toLocaleDateString()}
                  </div>
                )}
              </div>

              {/* View in GitLab - prominent link */}
              <a
                href={data.issue.webUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-800 rounded-lg hover:bg-orange-200 transition-colors font-medium"
              >
                <ExternalLink className="h-4 w-4" />
                View in GitLab
              </a>

              {/* Assignees with DM option */}
              <div>
                <h4 className="text-sm font-medium mb-2">Assignees</h4>
                {canAssign && (
                  <AssignPicker
                    gitlabProjectId={data.repo.gitlabProjectId}
                    iid={data.issue.iid}
                    assignees={data.issue.assignees.map(a => a.gitlabUsername)}
                    open
                    className="mb-2"
                  />
                )}
                {data.issue.assignees.length === 0 ? (
                  !canAssign && <p className="text-sm text-orange-600">No assignees</p>
                ) : (
                  <div className="space-y-2">
                    {data.issue.assignees.map(assignee => (
                      <div key={assignee.gitlabId} className="flex items-center justify-between p-2 bg-muted rounded">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          <span>{assignee.name} (@{assignee.gitlabUsername})</span>
                        </div>
                        {assignee.localUser?.canDM && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedAssignee(assignee.localUser!.id)}
                          >
                            <MessageSquare className="h-4 w-4 mr-1" />
                            DM
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Related MRs */}
            {data.mergeRequests.length > 0 && (
              <div className="p-4 border-b">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <GitPullRequest className="h-4 w-4" />
                  Related Merge Requests ({data.mergeRequests.length})
                </h4>
                <div className="space-y-2">
                  {data.mergeRequests.map(mr => (
                    <div key={mr.id} className="p-2 bg-muted rounded text-sm">
                      <div className="flex items-center gap-2">
                        <StateBadge state={mr.state} />
                        <a
                          href={mr.webUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline truncate"
                        >
                          !{mr.iid} {mr.title}
                        </a>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {mr.sourceBranch} → {mr.targetBranch}
                        {mr.mergedAt && ` • Merged ${formatRelativeTime(mr.mergedAt)}`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Branch activity */}
            {data.branchActivity.length > 0 && (
              <div className="p-4 border-b">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <GitCommit className="h-4 w-4" />
                  Recent Activity
                </h4>
                {data.branchActivity.map(branch => (
                  <div key={branch.branch} className="mb-4">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                      <GitBranch className="h-3 w-3" />
                      {branch.branch}
                    </div>
                    <div className="space-y-1 pl-4 border-l">
                      {branch.commits.slice(0, 5).map(commit => (
                        <div key={commit.sha} className="text-sm">
                          <a
                            href={commit.webUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {commit.sha.substring(0, 7)}
                          </a>
                          <span className="text-muted-foreground"> - {commit.title}</span>
                        </div>
                      ))}
                      {branch.reviews.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {branch.reviews.map(review => (
                            <div key={review.id} className="flex items-center gap-2 text-sm">
                              <Star className="h-3 w-3 text-yellow-500" />
                              <span>Score: {review.score}/100</span>
                              <span className="text-muted-foreground truncate">{review.summary}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Comments from GitLab */}
            <div className="p-4 border-b">
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Comments ({comments?.length || 0})
              </h4>
              {commentsLoading ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner className="h-5 w-5" />
                </div>
              ) : comments && comments.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {comments.map(c => (
                    <div key={c.id} className="p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-3 w-3 text-primary" />
                        </div>
                        <span className="text-sm font-medium">{c.author.name}</span>
                        <span className="text-xs text-muted-foreground">@{c.author.username}</span>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {formatRelativeTime(c.createdAt)}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground whitespace-pre-wrap pl-8">
                        {c.body}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-2">No comments yet</p>
              )}
            </div>

            {/* Add comment */}
            <div className="p-4 border-b">
              <h4 className="text-sm font-medium mb-2">Add Comment to GitLab</h4>
              <div className="flex gap-2">
                <Input
                  placeholder="Write a comment..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
                <Button onClick={handleAddComment} disabled={!comment.trim() || addCommentMutation.isPending}>
                  {addCommentMutation.isPending ? <LoadingSpinner className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              {notice && <p className="mt-2 text-xs text-amber-700">{notice}</p>}
            </div>

            {/* DM Modal */}
            {selectedAssignee && (
              <div className="p-4 border-b bg-blue-50">
                <h4 className="text-sm font-medium mb-2">Send Direct Message</h4>
                <div className="flex gap-2">
                  <Input
                    placeholder="Write your message..."
                    value={dmContent}
                    onChange={(e) => setDmContent(e.target.value)}
                  />
                  <Button onClick={handleSendDM} disabled={!dmContent.trim() || sendDMMutation.isPending}>
                    {sendDMMutation.isPending ? <LoadingSpinner className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                  </Button>
                  <Button variant="outline" onClick={() => setSelectedAssignee(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            Issue not found
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Main Project Dashboard component
 */
export default function ProjectDashboard() {
  const { projectId } = useParams<{ projectId: string }>();
  const [selectedIssue, setSelectedIssue] = useState<{ repoId: string; iid: number } | null>(null);
  const [filter, setFilter] = useState<'all' | 'issues' | 'mrs'>('all');

  const { data, isLoading, error } = useProjectDashboard(projectId, {
    issueState: 'opened',
    mrState: 'opened',
  });

  if (!projectId) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">No project selected</p>
        <Link to="/projects" className="text-primary hover:underline">
          View all projects
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
        <p className="text-red-600">Failed to load project dashboard</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{data.project.name}</h1>
          <p className="text-muted-foreground">
            {data.project.code} • {data.repos.length} repositories
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {data.repos.map(repo => (
            <a
              key={`new-${repo.id}`}
              // GitLab's new issue form with the AI task template filled in (no template file needed in the project)
              href={newIssueUrl(repo.gitlabUrl)}
              target="_blank"
              rel="noopener noreferrer"
              title={`New issue in ${repo.name}, with the AI task template`}
              className="inline-flex items-center gap-1 px-3 py-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 text-sm"
            >
              <Plus className="h-4 w-4" />
              New issue{data.repos.length > 1 ? ` · ${repo.name}` : ''}
            </a>
          ))}
          {data.repos.map(repo => (
            <a
              key={repo.id}
              href={repo.gitlabUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1 rounded bg-muted hover:bg-muted/80 text-sm"
            >
              <FolderGit2 className="h-4 w-4" />
              {repo.name}
              <ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Open Issues" value={data.summary.openIssues} icon={AlertCircle} />
        <StatCard label="Stale Issues" value={data.summary.staleIssues} icon={AlertTriangle} variant="warning" />
        <StatCard label="Open MRs" value={data.summary.openMergeRequests} icon={GitPullRequest} />
        <StatCard label="Draft MRs" value={data.summary.draftMergeRequests} icon={GitPullRequest} variant="default" />
        <StatCard label="Avg Review Score" value={data.summary.avgReviewScore} icon={Star} variant="success" />
      </div>

      {/* Stale issues alert */}
      <StaleIssuesAlert
        issues={data.staleIssues}
        onSelect={(issue) => {
          const repo = data.repos.find(r => r.name === issue.repoName);
          if (repo) {
            setSelectedIssue({ repoId: repo.id, iid: issue.iid });
          }
        }}
      />

      {/* Filter tabs */}
      <div className="flex gap-2 border-b">
        <button
          className={`px-4 py-2 border-b-2 ${filter === 'all' ? 'border-primary text-primary' : 'border-transparent'}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button
          className={`px-4 py-2 border-b-2 ${filter === 'issues' ? 'border-primary text-primary' : 'border-transparent'}`}
          onClick={() => setFilter('issues')}
        >
          Issues ({data.summary.openIssues})
        </button>
        <button
          className={`px-4 py-2 border-b-2 ${filter === 'mrs' ? 'border-primary text-primary' : 'border-transparent'}`}
          onClick={() => setFilter('mrs')}
        >
          Merge Requests ({data.summary.openMergeRequests})
        </button>
      </div>

      {/* Main content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Issues/MRs list */}
        <div className="lg:col-span-2 space-y-4">
          {(filter === 'all' || filter === 'issues') && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  Open Issues
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {data.issues.filter(i => i.state === 'opened').length === 0 ? (
                  <p className="p-4 text-center text-muted-foreground">No open issues</p>
                ) : (
                  <div className="divide-y">
                    {data.issues
                      .filter(i => i.state === 'opened')
                      .slice(0, 10)
                      .map(issue => (
                        <IssueRow
                          key={`${issue.repoId}-${issue.iid}`}
                          issue={issue}
                          onClick={() => setSelectedIssue({ repoId: issue.repoId, iid: issue.iid })}
                        />
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {(filter === 'all' || filter === 'mrs') && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <GitPullRequest className="h-5 w-5" />
                  Open Merge Requests
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {data.mergeRequests.filter(mr => mr.state === 'opened').length === 0 ? (
                  <p className="p-4 text-center text-muted-foreground">No open merge requests</p>
                ) : (
                  <div className="divide-y">
                    {data.mergeRequests
                      .filter(mr => mr.state === 'opened')
                      .slice(0, 10)
                      .map(mr => (
                        <MRRow key={`${mr.repoId}-${mr.iid}`} mr={mr} />
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <IssuesByAssignee data={data.issuesByAssignee} />

          {/* Recent commits */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <GitCommit className="h-5 w-5" />
                Recent Commits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.recentCommits.slice(0, 5).map(commit => (
                  <div key={commit.sha} className="text-sm">
                    <a
                      href={commit.webUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-primary hover:underline"
                    >
                      {commit.shortSha}
                    </a>
                    <span className="text-muted-foreground ml-2 truncate">
                      {commit.title}
                    </span>
                    <div className="text-xs text-muted-foreground">
                      {commit.author?.displayName || 'Unknown'} • {formatRelativeTime(commit.committedAt)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent reviews */}
          {data.recentReviews.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Star className="h-5 w-5" />
                  Recent AI Reviews
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {data.recentReviews.slice(0, 5).map(review => (
                    <div key={review.id} className="text-sm p-2 bg-muted rounded">
                      <div className="flex items-center justify-between">
                        <span className="font-mono">{review.commitSha.substring(0, 7)}</span>
                        <span className={`font-medium ${
                          review.overallScore >= 80 ? 'text-green-600' :
                          review.overallScore >= 60 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {review.overallScore}/100
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{review.summary}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Issue detail panel */}
      {selectedIssue && projectId && (
        <IssueDetailPanel
          projectId={projectId}
          repoId={selectedIssue.repoId}
          issueIid={selectedIssue.iid}
          onClose={() => setSelectedIssue(null)}
        />
      )}
    </div>
  );
}
